import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export default function Apply() {
  const [form, setForm] = useState({
    business_name: '',
    website: '',
    full_name: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/submit-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-navy-800/50 border border-navy-700/30 flex items-center justify-center mx-auto mb-6">
            <Check size={24} className="text-green-400" />
          </div>
          <h1 className="font-serif text-bone-white text-2xl mb-3">Application Submitted</h1>
          <p className="font-sans text-navy-200/60 text-sm mb-8 leading-relaxed">
            Thank you for your interest in BonesPay. We will review your application and get back to you shortly.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-sm text-bone-white/50 hover:text-bone-white transition-colors uppercase tracking-wider"
          >
            Back to home
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  const inputClass =
    'w-full border border-navy-700/40 rounded-subtle px-4 py-3 bg-navy-900/30 text-bone-white font-sans text-sm focus:outline-none focus:border-navy-500 transition-colors placeholder:text-bone-white/20';
  const labelClass =
    'block font-mono text-[10px] text-bone-white/40 uppercase tracking-widest mb-2';

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-lg">
        <div className="text-center mb-10">
          <Link to="/" className="font-mono text-bone-white tracking-widest text-sm font-medium uppercase">
            BONESPAY
          </Link>
          <p className="mt-3 font-sans text-sm text-navy-200/50">
            Apply to become a merchant
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="border border-red-500/30 bg-red-500/10 rounded-subtle px-4 py-3">
              <p className="font-sans text-sm text-red-300">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Business Name *</label>
              <input
                type="text"
                name="business_name"
                required
                value={form.business_name}
                onChange={handleChange}
                placeholder="Acme Corp"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Website *</label>
              <input
                type="url"
                name="website"
                required
                value={form.website}
                onChange={handleChange}
                placeholder="https://example.com"
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Full Name *</label>
              <input
                type="text"
                name="full_name"
                required
                value={form.full_name}
                onChange={handleChange}
                placeholder="John Smith"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Email *</label>
              <input
                type="email"
                name="email"
                required
                value={form.email}
                onChange={handleChange}
                placeholder="you@company.com"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>
              Phone Number <span className="text-bone-white/20">(Optional)</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="+1 (555) 000-0000"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>
              Notes <span className="text-bone-white/20">(Optional)</span>
            </label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              placeholder="Tell us about your business and how you plan to use BonesPay..."
              rows={4}
              className={`${inputClass} resize-none`}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-bone-white text-navy-900 font-mono text-sm font-medium py-3.5 rounded-subtle hover:bg-navy-100 transition-colors disabled:opacity-50 uppercase tracking-wider flex items-center justify-center gap-2 group"
          >
            {loading ? (
              'Submitting...'
            ) : (
              <>
                Submit Application
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </form>

        <p className="mt-8 text-center font-sans text-sm text-bone-white/30">
          Already a merchant?{' '}
          <Link to="/login" className="text-bone-white/60 hover:text-bone-white transition-colors">
            Sign in
          </Link>
        </p>

        <div className="mt-10 border-t border-navy-700/20 pt-6">
          <p className="font-mono text-[10px] text-bone-white/15 uppercase tracking-widest text-center">
            Applications reviewed within 24-48 hours.
          </p>
        </div>
      </div>
    </div>
  );
}
